import styled from "@emotion/styled";
import { Table as BaseTable } from "antd";

import { COLORS, MEDIA_QUERIES } from "shared/config/constants";

interface StyledTableProps {
  alt?: boolean;
  expandable?: boolean;
  indent?: boolean;
  marginForPagination?: boolean;
  noBorderBottom?: boolean;
  noClickableRows?: boolean;
  withBorder?: boolean;
}

// eslint-disable-next-line vero/styled-tagged-template
const StyledTable = styled<typeof BaseTable, StyledTableProps>(BaseTable)(
  ({
    alt,
    expandable,
    indent,
    marginForPagination,
    noBorderBottom,
    noClickableRows,
    withBorder,
  }) => ({
    backgroundColor: "white",
    "& table": {
      borderCollapse: "collapse",
      fontSize: "15px",
      ...(withBorder && {
        border: `1px solid ${COLORS.borders}`,
      }),
    },
    "& thead > tr > th": {
      backgroundColor: `${COLORS.table.default.header.background}`,
      color: `${COLORS.typography.text}`,
      fontWeight: 300,
      padding: "18px 24px",
      ...(alt && {
        backgroundColor: `${COLORS.table.alt.header.background}`,
      }),
      [MEDIA_QUERIES.w.ltMd]: {
        /* Match body horizontal padding (14px) so columns align with data rows */
        padding: "14px",
      },
      "&:first-of-type": {
        ...(indent && {
          paddingLeft: "58px",
        }),
      },
      "&.ant-table-selection-column": {
        textAlign: "initial",
      },
    },
    "& tbody > tr > td": {
      fontWeight: 400,
      cursor: "pointer",
      paddingLeft: "24px",
      paddingRight: "24px",
      [MEDIA_QUERIES.w.ltMd]: {
        paddingLeft: "14px",
        paddingRight: "14px",
      },
      ...(noClickableRows && {
        cursor: "default",
      }),
      "&:first-of-type": {
        ...(indent && {
          paddingLeft: "58px",
        }),
      },
    },
    "& tbody > tr": {
      "&:not(.ant-table-expanded-row):hover": {
        boxShadow: noClickableRows ? "none" : `${COLORS.boxShadow}`,
        "& > td": {
          backgroundColor: `${COLORS.table.default.row.default.hover} !important`,
        },
      },
      "&.row-viewed": {
        backgroundColor: COLORS.table.default.row.viewed.background,
        "&:hover": {
          "& > td": {
            backgroundColor: `${COLORS.table.default.row.viewed.background} !important`,
          },
        },
      },
      "&:last-of-type > td": {
        ...(noBorderBottom && {
          borderBottom: "0 !important",
        }),
      },
      "&.ant-table-expanded-row": {
        td: {
          padding: 0,
        },
      },
      ...(expandable && {
        display: "grid",
        gridAutoFlow: "row",
        td: {
          width: "auto",
          "&.ant-table-row-expand-icon-cell": {
            backgroundColor: COLORS.table.alt.header.background,
            padding: "0 5px",
            "& > *": {
              width: "100%",
            },
          },
          "&:first-of-type": {
            order: 2,
          },
        },
        "&.ant-table-expanded-row": {
          td: {
            display: "flex",
            flexWrap: "wrap",
            paddingLeft: "24px",
            paddingRight: "24px",
            "&:first-of-type": {
              display: "none",
            },
          },
        },
      }),
    },
    ".chevron-icon": {
      "&:hover": {
        circle: {
          fill: COLORS.table.default.row.viewed.background,
        },
        cursor: "pointer",
      },
    },
    ".ant-pagination": {
      marginRight: "60px",
      [MEDIA_QUERIES.w.ltSm]: {
        marginRight: 0,
        float: "none",
        display: "flex",
        justifyContent: "center",
        ...(marginForPagination && {
          marginRight: 50,
        }),
      },
    },
    ".ant-table-placeholder": {
      borderBottom: 0,
    },
    ".ant-spin-dot": {
      width: "auto",
      height: "auto",
      transform: "translateX(-50%)",
    },
    ".ant-spin-container::after": {
      display: "none",
    },
    ".ant-spin-blur": {
      opacity: 1,
    },
    ".disabled-row": {
      backgroundColor: "#F9FAFC",
      color: "#807E8D",
      pointerEvents: "none",
    },
    /* Wide tables: allow horizontal pan; sticky row-menu column keeps ⋯ visible */
    "& .ant-table-scroll, & .ant-table-content": {
      maxWidth: "100%",
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
    },
    "& thead > tr > th.dropdown-cell, & tbody > tr > td.dropdown-cell": {
      width: 48,
      minWidth: 48,
      maxWidth: 48,
      boxSizing: "border-box",
      paddingLeft: 6,
      paddingRight: 10,
      overflow: "visible",
      textAlign: "center",
      verticalAlign: "middle",
      position: "sticky",
      right: 0,
      boxShadow: "-6px 0 10px -8px rgba(26, 24, 37, 0.1)",
      [MEDIA_QUERIES.w.gteMd]: {
        width: 56,
        minWidth: 56,
        maxWidth: 56,
        paddingLeft: 8,
        paddingRight: 16,
      },
    },
    "& thead > tr > th.dropdown-cell": {
      zIndex: 4,
      backgroundColor: alt
        ? COLORS.table.alt.header.background
        : COLORS.table.default.header.background,
    },
    "& tbody > tr > td.dropdown-cell": {
      zIndex: 2,
      backgroundColor: "#fff",
    },
    "& tbody > tr:not(.ant-table-expanded-row):hover > td.dropdown-cell": {
      backgroundColor: `${COLORS.table.default.row.default.hover} !important`,
    },
    "& tbody > tr.row-viewed > td.dropdown-cell": {
      backgroundColor: COLORS.table.default.row.viewed.background,
    },
    "& tbody > tr.row-viewed:hover > td.dropdown-cell": {
      backgroundColor: `${COLORS.table.default.row.viewed.background} !important`,
    },
    "& tbody > tr.disabled-row > td.dropdown-cell": {
      backgroundColor: "#F9FAFC",
    },
    "& thead > tr > th.dropdown-cell.labeled-dropdown-cell, & tbody > tr > td.dropdown-cell.labeled-dropdown-cell":
      {
        width: 80,
        minWidth: 80,
        maxWidth: 80,
        [MEDIA_QUERIES.w.gteMd]: {
          width: 96,
          minWidth: 96,
          maxWidth: 96,
          paddingLeft: 16,
          paddingRight: 16,
        },
      },
    "& thead > tr > th.dropdown-cell.labeled-dropdown-cell": {
      textAlign: "left",
      whiteSpace: "nowrap",
    },
  })
);

const Icon = styled("span")({
  verticalAlign: "sub",
  marginLeft: "7px",
});

const EmptyText = styled("div")(() => ({
  marginTop: "-1px",
  padding: "70px",
}));

export { EmptyText, StyledTable, Icon };
